import { ProjectGroupModal } from "@/components/projects/ProjectGroupModal";
import { SlideTagChips } from "@/components/slides/list/SlideTagChips";
import { Sortable } from "@/lib/dnd/components/Sortable";
import { type ProjectGroupItem, useProjectGroups } from "@/lib/hooks/projects/useProjectGroups";
import { Button, Checkbox } from "@ai-teach/ui-web";
import { useState } from "react";
import {
  HiChevronDown,
  HiChevronRight,
  HiOutlineMenu,
  HiOutlinePencil,
  HiOutlineTrash,
} from "react-icons/hi";

/** One group at rest: the tags it collects, plus its edit, delete and ordering controls. */
function GroupRow({
  group,
  onEdit,
  onDelete,
  onToggleRandomise,
}: {
  group: ProjectGroupItem;
  onEdit: () => void;
  onDelete: () => void;
  onToggleRandomise: (randomiseOrder: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-900 cursor-grab active:cursor-grabbing">
      <HiOutlineMenu className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="font-semibold text-sm min-w-32">{group.name}</span>
      <div className="flex flex-wrap items-center gap-1.5 flex-1">
        {group.tagIds.length > 0 ? (
          <SlideTagChips tagIds={group.tagIds} />
        ) : (
          <span className="text-xs text-gray-500">No tags</span>
        )}
      </div>
      <label className="flex items-center gap-1.5 text-xs shrink-0 text-secondary-200 cursor-pointer">
        <Checkbox
          checked={group.randomiseOrder}
          onChange={(checked) => onToggleRandomise(checked)}
          size="sm"
        />
        Random
      </label>
      <button
        type="button"
        onClick={onEdit}
        className="text-gray-400 hover:text-primary-500 shrink-0"
        aria-label={`Edit group ${group.name}`}
      >
        <HiOutlinePencil className="w-5 h-5" />
      </button>
      <button
        type="button"
        onClick={onDelete}
        className="text-gray-400 hover:text-red-500 shrink-0"
        aria-label={`Delete group ${group.name}`}
      >
        <HiOutlineTrash className="w-5 h-5" />
      </button>
    </div>
  );
}

/** Groups for a project: its own definitions, covering the lessons directly under it. */
export function ProjectGroupsSection({ projectId }: { projectId: string }) {
  const {
    groups,
    useGroup,
    loading,
    adding,
    updating,
    addGroup,
    updateGroup,
    deleteGroup,
    setUseGroup,
    reorderGroups,
  } = useProjectGroups(projectId);

  const [expanded, setExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ProjectGroupItem | null>(null);

  const openModal = (group: ProjectGroupItem | null) => {
    setEditingGroup(group);
    setModalOpen(true);
  };

  // `skills` is omitted on purpose: the API derives it from the chosen tags, so the group
  // stays readable by b2b-mobile, which still groups by skill.
  const handleSubmit = (draft: { name: string; tagIds: string[]; randomiseOrder: boolean }) =>
    editingGroup ? updateGroup(editingGroup.id, draft) : addGroup(draft);

  const toggleRandomise = (group: ProjectGroupItem, randomiseOrder: boolean) =>
    updateGroup(group.id, { name: group.name, tagIds: [...group.tagIds], randomiseOrder });

  return (
    <div className="mb-12 border border-gray-200 dark:border-gray-800 rounded-lg p-5">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          className="flex items-center gap-2 text-secondary-200"
          aria-expanded={expanded}
        >
          {!expanded ? (
            <HiChevronRight className="w-5 h-5" />
          ) : (
            <HiChevronDown className="w-5 h-5" />
          )}
          <h2 className="h4">Groups</h2>
          <span className="text-sm">({groups.length})</span>
        </button>

        <label
          className="flex items-center gap-2 text-sm text-secondary-200 cursor-pointer"
          onClick={() => setUseGroup(!useGroup)}
        >
          <Checkbox checked={useGroup} onChange={(checked) => setUseGroup(checked)} size="sm" />
          Use groups
        </label>
      </div>

      {!expanded ? null : (
        <div className="mt-4">
          {loading && groups.length === 0 ? (
            <p className="text-sm">Loading groups...</p>
          ) : groups.length === 0 ? (
            <p className="text-sm">No groups yet.</p>
          ) : (
            <div className="flex flex-col gap-2">
              <Sortable items={groups} onReorder={(newItems) => reorderGroups(newItems)}>
                {(group) => (
                  <GroupRow
                    group={group}
                    onEdit={() => openModal(group)}
                    onDelete={() => deleteGroup(group.id)}
                    onToggleRandomise={(randomiseOrder) => toggleRandomise(group, randomiseOrder)}
                  />
                )}
              </Sortable>
            </div>
          )}

          <div className="mt-4">
            <Button size="sm" onClick={() => openModal(null)}>
              Add group
            </Button>
          </div>
        </div>
      )}

      <ProjectGroupModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        group={editingGroup}
        saving={editingGroup ? updating : adding}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
